import React, { useEffect, useRef, useState } from "react";
import styles from "./DateWheelPicker.module.css";

interface Props {
  onSelect: (date: Date) => void;
  initialDate?: Date;
}

const ITEM_HEIGHT = 40;

const DateWheelPicker: React.FC<Props> = ({ onSelect, initialDate }) => {
  const normalizedInitialDate = new Date(
    initialDate?.getFullYear() ?? new Date().getFullYear(),
    initialDate?.getMonth() ?? new Date().getMonth(),
    initialDate?.getDate() ?? new Date().getDate(),
    12,
    0,
    0
  );

  const [day, setDay] = useState(normalizedInitialDate.getDate());
  const [month, setMonth] = useState(normalizedInitialDate.getMonth());
  const [year, setYear] = useState(normalizedInitialDate.getFullYear());

  const dayRef = useRef<HTMLUListElement>(null);
  const monthRef = useRef<HTMLUListElement>(null);
  const yearRef = useRef<HTMLUListElement>(null);

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    "Янв",
    "Фев",
    "Мар",
    "Апр",
    "Май",
    "Июн",
    "Июл",
    "Авг",
    "Сен",
    "Окт",
    "Ноя",
    "Дек",
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  const centerScroll = (ref: React.RefObject<HTMLUListElement>, index: number) => {
    ref.current?.scrollTo({ top: index * ITEM_HEIGHT, behavior: "smooth" });
  };

  // Центрируем при открытии
  useEffect(() => {
    centerScroll(dayRef, day - 1);
    centerScroll(monthRef, month);
    centerScroll(yearRef, years.indexOf(year));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce для плавного прилипание
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const debounce = <T extends (...args: any[]) => void>(func: T, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: Parameters<T>): void => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const handleSnap = (
    ref: React.RefObject<HTMLUListElement>,
    items: number[],
    setter: (val: number) => void
  ) => {
    if (!ref.current) return;
    const index = Math.round(ref.current.scrollTop / ITEM_HEIGHT);
    setter(items[index]);
    centerScroll(ref, index);
  };

  const handleSnapDebounced = debounce(handleSnap, 100);

  useEffect(() => {
    const selectedDate = new Date(year, month, day, 12, 0, 0);
    onSelect(selectedDate);
  }, [day, month, year, onSelect]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.fadeTop}></div>

      <ul
        ref={dayRef}
        className={styles.wheel}
        onScroll={() => handleSnapDebounced(dayRef, days, setDay)}
      >
        <li className={styles.stub}></li>
        <li className={styles.stub}></li>
        {days.map((d) => (
          <li key={d} className={d === day ? styles.active : ""}>
            {d}
          </li>
        ))}
        <li className={styles.stub}></li>
        <li className={styles.stub}></li>
      </ul>

      <ul
        ref={monthRef}
        className={styles.wheel}
        onScroll={() =>
          handleSnapDebounced(
            monthRef,
            months.map((_, i) => i),
            setMonth
          )
        }
      >
        <li className={styles.stub}></li>
        <li className={styles.stub}></li>
        {months.map((m, i) => (
          <li key={m} className={i === month ? styles.active : ""}>
            {m}
          </li>
        ))}
        <li className={styles.stub}></li>
        <li className={styles.stub}></li>
      </ul>

      <ul
        ref={yearRef}
        className={styles.wheel}
        onScroll={() => handleSnapDebounced(yearRef, years, setYear)}
      >
        <li className={styles.stub}></li>
        <li className={styles.stub}></li>
        {years.map((y) => (
          <li key={y} className={y === year ? styles.active : ""}>
            {y}
          </li>
        ))}
        <li className={styles.stub}></li>
        <li className={styles.stub}></li>
      </ul>

      <div className={styles.highlight}></div>
      <div className={styles.fadeBottom}></div>
    </div>
  );
};

export default DateWheelPicker;
